import { useCallback, useEffect, useState } from 'react'
import { Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { AdminProfitByUserRow, GlobalProfitByUser, ProfitArchive } from '../../api/adminTypes'
import { formatNumberByCoin } from '../trading/coinConfigLabels'
import { AdminNotice } from './adminShared'
import { describeAdminError } from './adminHelpers'
const DEFAULT_EXCHANGE = 'binance'

/**
 * Equivalent of legacy `ProfitStatisticsComponent` (routes
 * `admin/statistics/yield` AND `admin/statistics/profit` — the SAME
 * component for both, per the audit). Two contract endpoints:
 *  - #25 `.../global-profit/byuser/{resolution}/{coin2}` — real, EVERY
 *    user's profit, grouped (the fleet-wide "yield" view);
 *  - #11 `/admin/statistics/profit` — ARCHIVE, a 30-day zero-history feed
 *    (the writer cron was disabled in legacy too) — shown collapsed since
 *    it is expected to be empty in production as well as here.
 */
export function AdminProfitStatisticsPage() {
  const [exchangeId, setExchangeId] = useState(DEFAULT_EXCHANGE)
  const [resolution, setResolution] = useState<'daily' | 'hourly'>('daily')
  const [coin2, setCoin2] = useState('USDT')
  const [days, setDays] = useState(30)
  const [byUser, setByUser] = useState<GlobalProfitByUser | null>(null)
  const [feedback, setFeedback] = useState<ReturnType<typeof describeAdminError> | undefined>()
  const [archive, setArchive] = useState<ProfitArchive | null>(null)
  const [showArchive, setShowArchive] = useState(false)

  const load = useCallback((ex: string, res: 'daily' | 'hourly', c2: string, d: number) => {
    setByUser(null)
    setFeedback(undefined)
    adminClient
      .globalProfitByUser(ex, res, c2, { days: d })
      .then((result) => setByUser(result))
      .catch((err) => setFeedback(describeAdminError(err)))
  }, [])

  useEffect(() => {
    load(exchangeId, resolution, coin2, days)
    adminClient.profitArchive().then(setArchive).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns: Column<AdminProfitByUserRow>[] = [
    { key: 'email', label: 'E-mail', align: 'left', sortValue: (r) => r.email },
    { key: 'profit', label: 'Profit', align: 'right', numeric: true, sortValue: (r) => r.profit, format: (_v, r) => formatNumberByCoin(r.profit, coin2), cellClass: (v) => (Number(v) < 0 ? 'portal-pnl-negative' : Number(v) > 0 ? 'portal-pnl-positive' : undefined) },
    { key: 'positiveProfit', label: 'Positive', align: 'right', numeric: true, sortValue: (r) => r.positiveProfit, format: (_v, r) => formatNumberByCoin(r.positiveProfit, coin2) },
    { key: 'loss', label: 'Loss', align: 'right', numeric: true, sortValue: (r) => r.loss, format: (_v, r) => formatNumberByCoin(r.loss, coin2) },
    { key: 'systemCommission', label: 'System commission', align: 'right', numeric: true, sortValue: (r) => r.systemCommission, format: (_v, r) => formatNumberByCoin(r.systemCommission, coin2) },
    { key: 'strategyCommission', label: 'Strategy commission', align: 'right', numeric: true, sortValue: (r) => r.strategyCommission, format: (_v, r) => formatNumberByCoin(r.strategyCommission, coin2) },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — global profit / yield</h1>
      </div>
      <div className="portal-card">
        <form
          className="portal-form"
          onSubmit={(e) => {
            e.preventDefault()
            load(exchangeId, resolution, coin2, days)
          }}
        >
          <label htmlFor="profit-exchange">Exchange</label>
          <input id="profit-exchange" value={exchangeId} onChange={(e) => setExchangeId(e.target.value)} />
          <label htmlFor="profit-resolution">Resolution</label>
          <select id="profit-resolution" value={resolution} onChange={(e) => setResolution(e.target.value as 'daily' | 'hourly')}>
            <option value="daily">Daily</option>
            <option value="hourly">Hourly</option>
          </select>
          <label htmlFor="profit-coin2">Quote asset</label>
          <input id="profit-coin2" value={coin2} onChange={(e) => setCoin2(e.target.value)} />
          <label htmlFor="profit-days">Days</label>
          <input id="profit-days" type="number" min={1} value={days} onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))} />
          <button type="submit">Reload</button>
        </form>
        <AdminNotice feedback={feedback} />
        {byUser === null && !feedback && <p>Loading…</p>}
        {byUser !== null && (
          <>
            <dl className="portal-kv">
              <dt>All users, total profit</dt>
              <dd>{formatNumberByCoin(byUser.all.profit, coin2)}</dd>
            </dl>
            <div className="portal-table-scroll">
              <Table columns={columns} rows={byUser.byUser} rowKey={(r) => r.userId} />
            </div>
          </>
        )}
        {archive && (
          <>
            <button type="button" onClick={() => setShowArchive((v) => !v)}>
              {showArchive ? 'hide' : 'show'} legacy 30-day archive (expected empty)
            </button>
            {showArchive && (
              <p className="portal-text-muted">
                Total: {archive.total.profit} over {archive.interval} — last day by user: {archive.lastDayByUser.length} rows
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
